import { Box, Card, CardContent, Typography } from "@mui/material";
import type { JSX, ReactNode } from "react";

/**
 * Layout shells for the marketing surfaces, extracted from
 * `marketing/shared.tsx`: the two-rail cockpit grid, the dense section-card
 * header, and the upstream-error banner.
 */

/**
 * Two balanced vertical rails for the agent cockpit / knowledge surfaces.
 *
 * The naïve "drop every card into one `repeat(2,1fr)` grid with
 * `alignItems:'start'`" layout looks broken the moment two cards in the same
 * row differ in height: the short card leaves a tall dead gap before the next
 * full-width row. Instead, each rail is its OWN single-column stack, so cards
 * pack top-down and a short card's neighbour slides straight up under it. The
 * two rails only top-align with each other, so leftover slack lands harmlessly
 * at the bottom of the shorter rail.
 *
 * Below `lg` the rails collapse into one column rendering `left` then `right`.
 * Full-width cards (heroes, footers, wide panels) belong OUTSIDE this.
 */
export function CardRails({
  left,
  right,
}: {
  left: ReactNode;
  right: ReactNode;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { lg: "repeat(2, 1fr)" },
        alignItems: "start",
      }}
    >
      <Box sx={{ display: "grid", gap: 2, alignContent: "start" }}>{left}</Box>
      <Box sx={{ display: "grid", gap: 2, alignContent: "start" }}>{right}</Box>
    </Box>
  );
}

/**
 * Dense section-card header used across the marketing cards. A 14px/600 title +
 * 12px muted subtitle stacked on the left, optional trailing slot on the right,
 * separated from the body by a hairline divider.
 */
export function SectionCardHeader({
  title,
  subtitle,
  trailing,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        borderBottom: 1,
        borderColor: "divider",
        px: 2.5,
        py: 2,
      }}
    >
      <Box sx={{ display: "grid", gap: 0.25 }}>
        <Typography
          component="div"
          sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography component="div" sx={{ fontSize: 12, color: "text.secondary" }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {trailing ? <Box sx={{ alignSelf: "center" }}>{trailing}</Box> : null}
    </Box>
  );
}

interface MarketingErrorDetails {
  reason?: string;
  url?: string;
  upstream_status?: number;
  upstream_body?: unknown;
}

export function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const msg =
    error && typeof error === "object" && "message" in error
      ? (error as Error).message
      : String(error);
  // The dashboard server's marketing proxy nests diagnostic fields under
  // `details` (see `apps/server/src/routes/marketing.ts`) so they
  // survive the React `parseResponse` and land on `ApiError.details`.
  // Render whatever made it through so the staff user sees the *cause*
  // of a 5xx (status + body excerpt) instead of a generic red blob.
  const details =
    error && typeof error === "object" && "details" in error
      ? ((error as { details?: MarketingErrorDetails }).details ?? {})
      : {};
  const code =
    error && typeof error === "object" && "code" in error
      ? ((error as { code?: string }).code ?? null)
      : null;
  return (
    <Card>
      <CardContent data-testid="marketing-error-banner" sx={{ fontSize: 14 }}>
        <Typography sx={{ fontWeight: 500, color: "error.main" }}>
          Marketing app unreachable
        </Typography>
        <Typography sx={{ mt: 0.5, color: "text.secondary" }}>
          <Box component="span" sx={{ fontFamily: "ui-monospace, monospace" }}>
            {msg}
          </Box>
        </Typography>
        {(code || details.reason || details.url || details.upstream_status !== undefined) && (
          <Box
            component="dl"
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: "max-content 1fr",
              columnGap: 1.5,
              rowGap: 0.5,
              fontSize: 12,
            }}
          >
            {code && (
              <>
                <Box component="dt" sx={{ color: "text.secondary" }}>code</Box>
                <Box component="dd" sx={{ m: 0 }}>
                  <code data-testid="marketing-error-code">{code}</code>
                </Box>
              </>
            )}
            {details.reason && (
              <>
                <Box component="dt" sx={{ color: "text.secondary" }}>reason</Box>
                <Box component="dd" sx={{ m: 0 }}>
                  <code>{details.reason}</code>
                </Box>
              </>
            )}
            {details.url && (
              <>
                <Box component="dt" sx={{ color: "text.secondary" }}>upstream URL</Box>
                <Box component="dd" sx={{ m: 0, wordBreak: "break-all" }}>
                  <code>{details.url}</code>
                </Box>
              </>
            )}
            {typeof details.upstream_status === "number" && (
              <>
                <Box component="dt" sx={{ color: "text.secondary" }}>upstream status</Box>
                <Box component="dd" sx={{ m: 0 }}>
                  <code data-testid="marketing-error-upstream-status">
                    {details.upstream_status}
                  </code>
                </Box>
              </>
            )}
          </Box>
        )}
        {details.upstream_body !== undefined && (
          <Box component="details" sx={{ mt: 1.5, fontSize: 12 }}>
            <Box component="summary" sx={{ cursor: "pointer", color: "text.secondary" }}>
              upstream body
            </Box>
            <Box
              component="pre"
              sx={{
                mt: 0.5,
                maxHeight: 192,
                overflow: "auto",
                borderRadius: 1,
                bgcolor: "surfaceSoft",
                p: 1,
                fontFamily: "ui-monospace, monospace",
                fontSize: "11px",
              }}
              data-testid="marketing-error-upstream-body"
            >
              {typeof details.upstream_body === "string"
                ? details.upstream_body
                : JSON.stringify(details.upstream_body, null, 2)}
            </Box>
          </Box>
        )}
        <Typography sx={{ mt: 1, color: "text.secondary", fontSize: 14 }}>
          Common causes: marketing app not started, dashboard{" "}
          <Box component="code" sx={{ fontFamily: "ui-monospace, monospace" }}>
            JWT_SECRET
          </Box>{" "}
          not matching the gateway's, or a stale browser session — try logging
          out and back in.
        </Typography>
      </CardContent>
    </Card>
  );
}
